import { useEffect, useMemo } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { betslipHasLiveSelection } from '@/domain/betslip-live';
import { useOptionalLiveFeed } from '@/live/LiveFeedProvider';
import { useLiveFeedSocket } from '@/live/useLiveFeedSocket';
import { betslipApplyLiveEnrichment } from './betslip-store';
import { useBetslipTicket } from './useBetslip';

/**
 * Keeps draft betslip odds / scores / closed state in sync with the live feed,
 * and opens the socket when the slip has live selections outside `/live`.
 */
export function useBetslipLiveSync(): void {
  const ticket = useBetslipTicket();
  const liveFeed = useOptionalLiveFeed();
  const isLiveRoute = useRouterState({
    select: (state) => {
      const pathname = state.location.pathname;
      return pathname.startsWith('/live') && !pathname.startsWith('/livecasino');
    },
  });
  const hasLiveSelections = useMemo(() => betslipHasLiveSelection(ticket), [ticket]);
  const slipEventIdsKey = useMemo(
    () => ticket.Items.map((item) => Number(item.Event.EventId)).join(','),
    [ticket],
  );

  // `/live` already opens the socket; this covers sports pages with live picks.
  useLiveFeedSocket(hasLiveSelections && !isLiveRoute);

  useEffect(() => {
    if (liveFeed) betslipApplyLiveEnrichment(liveFeed);
  }, [liveFeed, ticket]);

  useEffect(() => {
    if (!liveFeed || !slipEventIdsKey) return;
    const slipEventIds = new Set(slipEventIdsKey.split(',').map(Number));
    return liveFeed.subscribe((dirty) => {
      for (const eventId of dirty) {
        if (slipEventIds.has(eventId)) {
          betslipApplyLiveEnrichment(liveFeed);
          return;
        }
      }
    });
  }, [liveFeed, slipEventIdsKey]);
}
